import { DeviceSearchRow } from '../../types';
import {
  DeviceSearchParams,
  deviceSearchWire,
  fetchDeviceSearch,
} from '../../hooks/useDeviceSearch';
import { CsvValue, csvFilename, toCsv } from '../../utils/chartCsv';
import { ColumnDef, csvHeaderOf, csvValueOf } from './columnRegistry';

/**
 * FS-745 — the Device List download: every device the current search,
 * filters and sort produce, not just the page on screen, in the columns on
 * screen.
 */

/** device-data-api caps one /devices/search request at 200 rows. */
export const CSV_FETCH_PAGE = 200;

/**
 * Walk /devices/search a page at a time with the list's own filters and sort.
 * The server breaks sort ties on the device id, so the pages line up; a device
 * that moves between pages while the walk runs is kept once.
 *
 * `maxRows` stops the walk after that many rows from the top of the list
 * (FS-882's "Select all matching"); the CSV download walks to the end.
 *
 * `signal` (FS-889) stops the walk: the request in flight is aborted, a page
 * that still answers after that is dropped, and no further page is asked
 * for, so a walk started under an account or a view that is no longer on
 * screen never reaches its download or its selection.
 */
export const fetchAllDeviceRows = async (
  params: DeviceSearchParams,
  fetchPage = fetchDeviceSearch,
  maxRows = Number.POSITIVE_INFINITY,
  signal?: AbortSignal,
): Promise<DeviceSearchRow[]> => {
  const wire = deviceSearchWire({ ...params, page: 1 });
  const rows: DeviceSearchRow[] = [];
  const seen = new Set<number>();
  const stopped = () => new DOMException('The device list walk was stopped', 'AbortError');
  for (let offset = 0; offset < maxRows; offset += CSV_FETCH_PAGE) {
    if (signal?.aborted) throw stopped();
    const limit = Math.min(CSV_FETCH_PAGE, maxRows - offset);
    const data = await fetchPage({ ...wire, limit, offset }, signal);
    // Checked again after the wait: a transport that answers instead of
    // rejecting on abort must not deliver the page.
    if (signal?.aborted) throw stopped();
    const page = data.devices ?? [];
    for (const row of page) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    if (page.length < limit || offset + limit >= (data.total ?? 0)) break;
  }
  return rows;
};

/**
 * The CSV text for these rows in these columns. `pageCells` supplies the
 * columns whose cell the page renders itself (Source reads the live Firestore
 * enrichment, which the registry cannot see).
 */
export const deviceListCsv = (
  rows: DeviceSearchRow[],
  columns: ColumnDef[],
  pageCells: Record<string, (row: DeviceSearchRow) => CsvValue> = {},
): string =>
  toCsv(
    rows,
    columns.map((c) => ({
      header: csvHeaderOf(c),
      value: (row: DeviceSearchRow) => (pageCells[c.key] ?? ((r) => csvValueOf(c, r)))(row),
    })),
  );

/** `devices_2026-09-30.csv`, dated in the operator's own time zone. */
export const deviceListCsvFilename = (now: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return csvFilename(['devices', day]);
};
